/**
 * StaticExpenseList.jsx — Recurring/static expenses table
 *
 * Lifecycle: Active → Expiring → Archived
 *   Active:   no cancelledAt
 *   Expiring: cancelledAt set, expiresDate >= today (or no expiresDate yet)
 *   Archived: cancelledAt set, expiresDate < today
 *
 * Cancel: sets cancelledAt + computes expiresDate if missing
 * Delete: hard-removes from DB
 * Reactivate: clears cancelledAt
 */

import { useState, useMemo } from 'react';
import { fmtC, fmtD, cn, toLocalDateStr } from '../../lib/utils';
import { useStaticExpenses, useUpdateStaticExpense, useDeleteStaticExpense } from '../../hooks/useExpenses';
import { useToast } from '../layout/Toast';
import StaticExpenseForm from './StaticExpenseForm';

const STATUS_FILTERS = ['all', 'active', 'expiring', 'archived'];

function getStatus(e) {
  if (!e.cancelledAt) return 'active';
  if (!e.expiresDate) return 'expiring'; // cancelled but no end date computed yet
  const today = toLocalDateStr(new Date());
  return e.expiresDate >= today ? 'expiring' : 'archived';
}

function getStatusColor(status) {
  if (status === 'active') return 'bg-green';
  if (status === 'expiring') return 'bg-yellow-500';
  return 'bg-text-dim';
}

/** Compute the next charge occurrence for setting expiresDate on cancel */
function computeNextCharge(se) {
  const today = new Date();
  const freq = se.frequency || 'monthly';

  if (freq === 'one-time') return toLocalDateStr(today);

  if (freq === 'weekly') {
    // Next occurrence = today + up to 7 days
    if (se.chargeDate && se.chargeDate.length > 2) {
      const anchor = new Date(se.chargeDate + 'T12:00');
      const dayOfWeek = anchor.getDay();
      const d = new Date(today);
      while (d.getDay() !== dayOfWeek) d.setDate(d.getDate() + 1);
      return toLocalDateStr(d);
    }
    return toLocalDateStr(today);
  }

  // Monthly / quarterly / annual — find next charge day
  const anchorDay = se.chargeDate
    ? (se.chargeDate.length <= 2 ? parseInt(se.chargeDate) : new Date(se.chargeDate + 'T12:00').getDate())
    : 1;

  const step = freq === 'quarterly' ? 3 : (freq === 'annual' || freq === 'yearly') ? 12 : 1;
  let y = today.getFullYear();
  let m = today.getMonth();

  // Check current month first, then step forward
  for (let i = 0; i < 13; i += step) {
    const cm = m + i;
    const cy = y + Math.floor(cm / 12);
    const rm = cm % 12;
    const lastDay = new Date(cy, rm + 1, 0).getDate();
    const day = Math.min(anchorDay, lastDay);
    const candidate = new Date(cy, rm, day);
    if (candidate >= today) {
      return toLocalDateStr(candidate);
    }
  }
  return toLocalDateStr(today);
}

export default function StaticExpenseList({ onNewExpense }) {
  const { data: staticExpenses = [], isLoading, error } = useStaticExpenses();
  const updateMutation = useUpdateStaticExpense();
  const deleteMutation = useDeleteStaticExpense();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);

  // Annotate with status
  const annotated = useMemo(() =>
    staticExpenses.map((e) => ({ ...e, _status: getStatus(e) })),
    [staticExpenses]
  );

  // Counts
  const counts = useMemo(() => {
    const c = { all: 0, active: 0, expiring: 0, archived: 0 };
    annotated.forEach((e) => { c[e._status]++; c.all++; });
    return c;
  }, [annotated]);

  // Unique categories
  const categories = useMemo(() => {
    const cats = new Set();
    annotated.forEach((e) => { if (e.category) cats.add(e.category); });
    return ['all', ...Array.from(cats).sort()];
  }, [annotated]);

  // Filter
  const filtered = useMemo(() => {
    let result = annotated;

    if (statusFilter !== 'all') {
      result = result.filter((e) => e._status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      result = result.filter((e) => e.category === categoryFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          (e.description || '').toLowerCase().includes(q) ||
          (e.category || '').toLowerCase().includes(q) ||
          (e.method || '').toLowerCase().includes(q)
      );
    }

    // Active first, then expiring, then archived; within each group sort by description
    const order = { active: 0, expiring: 1, archived: 2 };
    result.sort((a, b) => order[a._status] - order[b._status] || (a.description || '').localeCompare(b.description || ''));

    return result;
  }, [annotated, search, statusFilter, categoryFilter]);

  const handleCancel = async (exp) => {
    if (!confirm('Cancel this recurring expense? It will continue charges until the expiration date.')) return;

    try {
      const updates = { id: exp.id, cancelledAt: new Date().toISOString() };
      if (!exp.expiresDate) {
        updates.expiresDate = computeNextCharge(exp);
      }
      await updateMutation.mutateAsync(updates);
      toast('Recurring expense cancelled');
    } catch (err) {
      toast('Failed to cancel expense', 'error');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await updateMutation.mutateAsync({ id, cancelledAt: null });
      toast('Recurring expense reactivated');
    } catch (err) {
      toast('Failed to reactivate expense', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Permanently delete this expense? This cannot be undone.')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast('Expense deleted');
    } catch (err) {
      toast('Failed to delete expense', 'error');
    }
  };

  if (error) {
    return (
      <div className="card p-4 text-red-accent text-sm">
        Failed to load recurring expenses: {error.message}
      </div>
    );
  }

  // Monthly total (active only)
  const monthlyTotal = annotated
    .filter((e) => e._status === 'active')
    .reduce((sum, e) => {
      const amt = parseFloat(e.amount) || 0;
      if (e.frequency === 'monthly') return sum + amt;
      if (e.frequency === 'annual' || e.frequency === 'yearly') return sum + amt / 12;
      if (e.frequency === 'quarterly') return sum + amt / 3;
      if (e.frequency === 'weekly') return sum + amt * 4.33;
      return sum + amt; // one-time: count as-is
    }, 0);

  const annualTotal = monthlyTotal * 12;
  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading text-text-primary">Recurring Expenses</h2>
        <button
          onClick={onNewExpense}
          className="px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors"
        >
          + Add Recurring
        </button>
      </div>

      {/* Monthly burn — prominent */}
      <div className="card p-4 flex items-center justify-between flex-wrap gap-2 border-l-4 border-accent">
        <div>
          <div className="text-2xs font-mono uppercase tracking-wider text-text-dim">Monthly Burn</div>
          <div className="text-3xl font-heading text-accent mt-0.5">{fmtC(monthlyTotal)}</div>
          <div className="text-2xs text-text-dim mt-0.5">{fmtC(annualTotal)} annualized · {counts.active} active</div>
        </div>
        <div className="flex items-center gap-3 text-2xs text-text-dim font-mono">
          <span>{counts.expiring} expiring</span>
          <span>·</span>
          <span>{counts.archived} archived</span>
        </div>
      </div>

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder="Search by description, category, or method..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={cn(
              'px-2.5 py-1 rounded text-2xs font-semibold transition-colors capitalize',
              statusFilter === f
                ? 'bg-accent text-bg'
                : 'bg-surface border border-border text-text-dim hover:text-text-primary'
            )}
          >
            {f} ({counts[f]})
          </button>
        ))}
        <span className="mx-1 text-border">|</span>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            className={cn(
              'px-2.5 py-1 rounded text-2xs font-semibold transition-colors capitalize',
              categoryFilter === c
                ? 'bg-accent text-bg'
                : 'bg-surface border border-border text-text-dim hover:text-text-primary'
            )}
          >
            {c === 'all' ? 'All Categories' : c}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto card p-0">
        {isLoading ? (
          <div className="p-4 text-center text-text-dim text-xs">Loading recurring expenses...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-text-dim text-xs">No recurring expenses found</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-2 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-text-dim w-5"></th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Description</th>
                <th className="px-3 py-2 text-right font-semibold text-text-dim">Amount</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Frequency</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Method</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Category</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Charge Day</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Expires</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Notes</th>
                <th className="px-3 py-2 text-center font-semibold text-text-dim">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((exp) => {
                const isArchived = exp._status === 'archived';
                const isExpiring = exp._status === 'expiring';

                if (editingId === exp.id) {
                  return (
                    <tr key={exp.id}>
                      <td colSpan={10} className="p-4 bg-surface-2">
                        <StaticExpenseForm
                          initialExpense={exp}
                          onSave={() => setEditingId(null)}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={exp.id}
                    className={cn(
                      'transition-colors',
                      isArchived ? 'bg-surface opacity-40' : isExpiring ? 'bg-yellow-500/5' : 'hover:bg-surface-2'
                    )}
                  >
                    {/* Status dot */}
                    <td className="px-3 py-2">
                      <span className={cn('inline-block w-2 h-2 rounded-full', getStatusColor(exp._status))} title={exp._status} />
                    </td>
                    <td className={cn('px-3 py-2', isArchived && 'line-through')}>
                      {exp.description || '—'}
                    </td>
                    <td className={cn('px-3 py-2 text-right font-semibold', isArchived ? 'text-text-dim' : 'text-accent')}>
                      {fmtC(exp.amount)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded text-2xs bg-surface border border-border text-text-dim">
                        {exp.frequency || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-primary">{exp.method || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded text-2xs bg-surface border border-border text-text-dim">
                        {exp.category || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-dim">
                      {exp.chargeDate
                        ? (exp.chargeDate.length <= 2 ? `${exp.chargeDate}${['st','nd','rd'][((+exp.chargeDate+90)%100-10)%10-1]||'th'}` : fmtD(exp.chargeDate))
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-text-dim">{fmtD(exp.expiresDate) || '—'}</td>
                    <td className="px-3 py-2 text-text-dim truncate max-w-[150px]" title={exp.notes || ''}>{exp.notes || '—'}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {exp._status === 'active' ? (
                        <>
                          <button onClick={() => setEditingId(exp.id)} disabled={isBusy} className="px-2 py-0.5 text-2xs text-accent hover:bg-accent/10 rounded transition-colors disabled:opacity-50">Edit</button>
                          <button onClick={() => handleCancel(exp)} disabled={isBusy} className="px-2 py-0.5 text-2xs text-yellow-500 hover:bg-yellow-500/10 rounded transition-colors disabled:opacity-50">Cancel</button>
                          <button onClick={() => handleDelete(exp.id)} disabled={isBusy} className="px-1.5 py-0.5 text-2xs text-red-accent hover:bg-red-accent/10 rounded transition-colors disabled:opacity-50 ml-1">🗑</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditingId(exp.id)} disabled={isBusy} className="px-2 py-0.5 text-2xs text-accent hover:bg-accent/10 rounded transition-colors disabled:opacity-50">Edit</button>
                          <button onClick={() => handleReactivate(exp.id)} disabled={isBusy} className="px-2 py-0.5 text-2xs text-green hover:bg-green/10 rounded transition-colors disabled:opacity-50">Reactivate</button>
                          <button onClick={() => handleDelete(exp.id)} disabled={isBusy} className="px-1.5 py-0.5 text-2xs text-red-accent hover:bg-red-accent/10 rounded transition-colors disabled:opacity-50 ml-1">🗑</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary footer */}
      <div className="flex items-center justify-between px-3 py-2 bg-surface border border-border rounded text-sm">
        <span className="text-text-dim font-semibold">Active recurring:</span>
        <span className="text-accent font-heading">{fmtC(monthlyTotal)}/month · {fmtC(annualTotal)}/year</span>
      </div>
    </div>
  );
}
